import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Injectable, NgModule, provideExperimentalZonelessChangeDetection } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { StoreModule } from '@ngrx/store';

import { AnalyticsClient, AnalyticsSegmentClient, RtAnalyticsModule } from '@recruitee/analytics';
import { resolveLayoutAsset } from '@recruitee/assets/theme-layouts';
import {
  AccountBase,
  APP_CONFIG,
  DetachedRouterOutlet,
  RtcErrorHandlerModule,
} from '@recruitee/common';
import { provideLayoutAssetsResolver } from '@recruitee/components/theme-picker';
import { CountryDataModule, injectCountryList } from '@recruitee/country-data';
import { I18nModule, LANGUAGE_CONFIG, LOCALE } from '@recruitee/i18n';
import {
  IconsContainerComponent,
  PHONE_INPUT_DATA,
  protectedControlsRootProviders,
  UIRootProviders,
} from '@recruitee/user-interface';

import { environment } from '../environments/environment';
import { AccountSetupRoutingModule } from './account-setup.routing.module';
import { AppComponent } from './app.component';
import { AppRevisionProvider, AppRevisionToken } from './app.revision';

export const defaultLocale = 'en';

@Injectable()
export class Account extends AccountBase {}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    ReactiveFormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
    RtAnalyticsModule.forRoot({
      disable: !environment.analytics?.isEnabled,
      segmentKey: environment.analytics?.key || '',
      trackRouteChange: false,
    }),
    BrowserModule,
    I18nModule.forPhraseApp({
      phraseAppProjectId: environment.i18n.phraseAppProjectId,
      editorEnabled: environment.i18n.editorEnabled,
    }),
    RtcErrorHandlerModule.forRoot(
      {
        enable: environment.angularProdMode,
        dsn: environment.sentry_dsn,
        revisionToken: AppRevisionToken,
      },
      Account,
    ),
    AccountSetupRoutingModule,
    StoreModule.forRoot({}),
    CountryDataModule,
    BrowserAnimationsModule,
    IconsContainerComponent,
    DetachedRouterOutlet,
  ],
  providers: [
    UIRootProviders(),
    Account,
    AppRevisionProvider,
    protectedControlsRootProviders(),
    { provide: LOCALE, useValue: defaultLocale },
    {
      provide: APP_CONFIG,
      useValue: {
        isE2e: !!window.Cypress,
      },
    },
    {
      provide: LANGUAGE_CONFIG,
      useValue: {
        defaultLanguage: 'en',
        languages: ['en', 'nl', 'de', 'fr', 'sv', 'fi'],
      },
    },
    {
      provide: PHONE_INPUT_DATA,
      useFactory: injectCountryList,
    },
    provideLayoutAssetsResolver(resolveLayoutAsset),
    provideHttpClient(withInterceptorsFromDi()),
    provideExperimentalZonelessChangeDetection(),
    { provide: AnalyticsClient, useClass: AnalyticsSegmentClient },
  ],
})
export class AppModule {}
